import React, { useEffect } from "react";
import { Box, Divider, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Page } from "../../components/Page";
import { WithAuthorization } from "../../context";
import StatusTab from "./components/StatusTab";
import SuppliersTab from "./components/SuppliersTab";
import DataTasksTab from "./components/DataTasksTab";

// "Supplier Data" subsection. Tabbed layout mirroring the AIR Disclosure
// Builder page (IssbRequirementsPage): a page title, a primary tab strip, a
// per-tab subtitle, then the tab panels. Tab state is held in the `?dtab=`
// search param so tabs are deep-linkable and survive refresh.
function SupplierData() {
  const { t } = useTranslation("translation", { keyPrefix: "supplyChain.supplierData" });
  const [searchParams, setSearchParams] = useSearchParams();

  const tabIndex = parseInt(searchParams.get("dtab"), 10) || 0;
  const [selectedTab, setSelectedTab] = React.useState(tabIndex);

  const setTabIndex = (index) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("dtab", index);
    setSearchParams(newParams);
    setSelectedTab(index);
  };

  useEffect(() => {
    setSelectedTab(parseInt(searchParams.get("dtab"), 10) || 0);
  }, [searchParams]);

  // Jump to the Data Tasks tab (index 2) with filter params pre-applied, so the
  // Status cards can deep-link into a filtered task view.
  const goToDataTasks = (filterParams = {}) => {
    const next = new URLSearchParams(searchParams);
    next.set("dtab", "2");
    // Clear any prior deep-link filter params before applying the new set.
    ["dtYear", "dtStatus", "dtOverdue"].forEach((k) => next.delete(k));
    Object.entries(filterParams).forEach(([k, v]) => { if (v != null) next.set(k, v); });
    setSearchParams(next);
    setSelectedTab(2);
  };

  return (
    <Page>
      <Box p={4} mx={{ base: 4, md: "10%", xl: "15%" }} bg="pureWhite">
        <Text fontSize="heading.3xl" fontWeight="700" color="darkTeal" mb={6}>
          {t("page.title")}
        </Text>
        <Tabs variant="primary" isLazy index={tabIndex} onChange={setTabIndex}>
          <TabList>
            <Tab>{t("tabs.status")}</Tab>
            <Tab>{t("tabs.suppliers")}</Tab>
            <Tab>{t("tabs.dataTasks")}</Tab>
          </TabList>

          <Divider />

          <Text maxW="75%" color="gray.500" fontSize="body.m" mt={4}>
            {t(`subtitles.${selectedTab}`)}
          </Text>

          <TabPanels>
            <TabPanel px={0}>
              <StatusTab onOpenTasks={goToDataTasks} />
            </TabPanel>
            <TabPanel px={0}>
              <SuppliersTab />
            </TabPanel>
            <TabPanel px={0}>
              <DataTasksTab />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Page>
  );
}

export default WithAuthorization(SupplierData);
